import React, {useMemo} from "react";
import {Box, IconButton, Toolbar, Tooltip, Typography, Unstable_Grid2 as Grid, useColorScheme} from "@mui/material";
import {CloseRounded, RestartAltOutlined} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {FullScreenButton} from "./full-screen-button";
import EnvService from "../../../../../core/services/env";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {InstallPWAButton} from "./install-pwa-button";
import {ColorModeButton} from "./color-mode";
import {CompactModeButton} from "./compact-mode-button";
import {SupportedLanguages} from "./supported-languages";
import {usePanelLayoutConfiguration} from "../../../../hooks/use-custom-saved-components-state/use-panel-layout-configuration";


export interface PanelSettingsDrawerProps {
    close: () => void;
}

export const PanelSettingsDrawer: React.FC<PanelSettingsDrawerProps> = ({
                                                                            close,
                                                                        }) => {
    const operatorAccessLevels = useAccessLevels();
    const {isLayoutCompact, locale, themeMode, setIsLayoutCompact, setThemeMode, setLocale} = usePanelLayoutConfiguration();
    const {setMode} = useColorScheme();
    const {i18n, t} = useTranslation("components", {keyPrefix: 'panel-sections.drawers.settings-drawer'});

    const accessToSupportedLanguages = useMemo(() => {
        return (
            operatorAccessLevels.packages.base.frenchLanguageSupport ||
            operatorAccessLevels.packages.base.spanishLanguageSupport
        )
    }, [operatorAccessLevels.packages.base.frenchLanguageSupport, operatorAccessLevels.packages.base.spanishLanguageSupport])

    /**
     * Restarts the theme configurations to default settings.
     */
    const restartCachedLayoutSettings = React.useCallback(async () => {
        setMode('system');
        void i18n.changeLanguage(EnvService.defaultLocale);
        setIsLayoutCompact(false);
        setThemeMode('system');
        setLocale(EnvService.defaultLocale);
    }, [i18n, setIsLayoutCompact, setLocale, setMode, setThemeMode])

    return (
        <>
            <Box sx={{position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto', viewTransitionName: "panel-settings-drawer"}}>
                <Toolbar disableGutters>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%', paddingInlineStart: 2, paddingInlineEnd: 1}}>
                        <Typography variant={'h6'} component={'h2'} sx={{fontWeight: 500}}>
                            {t(t => t.title)}
                        </Typography>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5}}>
                            <InstallPWAButton/>
                            <FullScreenButton/>
                            <Tooltip title={t(t => t.restartSettings)}>
                                <IconButton
                                    aria-label={t(t => t.restartSettings)}
                                    onClick={restartCachedLayoutSettings}
                                    color={'default'}
                                >
                                    <RestartAltOutlined/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t(t => t.close)}>
                                <IconButton
                                    aria-label={t(t => t.close)}
                                    onClick={close}
                                    color={'default'}
                                >
                                    <CloseRounded/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Toolbar>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 6, paddingInline: 2, marginBlock: 3}}>
                    <Grid container gap={2}>
                        <Grid xs={12}>
                            <ColorModeButton
                                colorMode={themeMode}
                                setColorMode={setThemeMode}
                            />
                        </Grid>
                        <Grid xs={6}>
                            <CompactModeButton
                                isInCompactMode={isLayoutCompact}
                                setIsInCompactMode={setIsLayoutCompact}
                            />
                        </Grid>
                    </Grid>
                    {
                        accessToSupportedLanguages &&
                        <SupportedLanguages
                            selectedLanguage={locale}
                            setCachedLayoutSettings={setLocale}
                        />
                    }
                </Box>
            </Box>
        </>
    );
}